import moment from 'moment';
import { FieldType } from 'onefaceui-react-components';
import defaultFetch from 'react/api/sagas/fetch/defaultFetch';
import { getLookupExtraParams } from 'react/utils/lookupUtils';
import {
  convertHourToSeconds,
  getDateFormatted,
  getLocation,
  getTimeInputFormat,
  isDate,
  isTime
} from 'react/utils/onefaceLibraryUtils';
import { getRouteStatusOptions } from 'react/utils/routeUtils';
import { getResourceTo } from 'react/utils/utils';

export const getEquipmentSizesAmount = (equipmentAssignments) => {
  const DEFAULT_SIZES_AMOUNT = {
    size1: 0,
    size2: 0,
    size3: 0
  };

  if (!equipmentAssignments) {
    return DEFAULT_SIZES_AMOUNT;
  }

  return equipmentAssignments.reduce((acc, item) => {
    const { size1 = 0, size2 = 0, size3 = 0 } = item.equipment.equipmentType;

    const updatedAcc = {
      size1: acc.size1 + size1,
      size2: acc.size2 + size2,
      size3: acc.size3 + size3
    };

    return updatedAcc;
  }, DEFAULT_SIZES_AMOUNT);
};

export const getSizesPercentage = (planned, equipmentAmount) => {
  if (planned === 0 || equipmentAmount === 0) return 0;
  if (planned >= equipmentAmount) return 0;

  return (planned / equipmentAmount) * 100;
};

export const getFieldType = (type) => {
  const types = {
    LOOKUP: FieldType.LOOKUP,
    LOOKUP_LIST: FieldType.LOOKUP_MULTISELECT,
    TEXT: FieldType.TEXT,
    DATE: FieldType.DATE,
    CHECKBOX: FieldType.CHECKBOX,
    DATE_TIME: FieldType.DATETIME,
    DISTANCE: FieldType.DECIMAL_RANGE,
    DECIMAL: FieldType.DECIMAL,
    PICKLIST: FieldType.SELECT,
    SECONDS: FieldType.TIME,
    INTEGER: FieldType.NUMBER,
    TAG: FieldType.MULTIPLE_SELECT
  };

  return types[type] || FieldType.TEXT;
};

export const addIntervalFields = (fields) => {
  const fieldForInterval = [
    FieldType.DATETIME,
    FieldType.DATE,
    FieldType.DECIMAL,
    FieldType.NUMBER,
    FieldType.TIME
  ];

  const fieldsWithInterval = fields.map((field) => {
    if (fieldForInterval.includes(field.type)) {
      return { ...field, generateRangeField: true };
    }

    return field;
  });

  return fieldsWithInterval;
};

export const mapFieldsToFilterBuilder = (field, fieldsData = []) => {
  const fieldForDateTimeOrTime = [
    FieldType.DATETIME,
    FieldType.TIME_RANGE,
    FieldType.TIME
  ];
  const fieldDates = [FieldType.DATE, FieldType.DATE_RANGE];
  const userPreferences = {
    timeFormat: getTimeInputFormat(
      Monet.Session.User.preferedLocale.timeFormat
    ),
    dateFormat: getDateFormatted(),
    locale: getLocation(Monet.Session.User.preferedLocale.language)
  };

  const currentField = {
    ...field,
    label: getResourceTo(field.label),
    type: getFieldType(field.type)
  };

  if (field?.lookupEntity) {
    currentField.extra = getLookupExtraParams({
      entity: field.lookupEntity.name,
      defaultField: field?.lookupEntityColumn,
      dataTestId: `${field.lookupEntity.name}-field`,
      autoComplete: 'off',
      onBeforeFilter: (term) => `*${term}*`
    });

    currentField.lookupParams = {
      column: 'id'
    };
  }

  if (field.type === 'PICKLIST') {
    if (field?.meta) {
      const { hasMany } = JSON.parse(field.meta);

      const actualField = fieldsData.find(
        (field) => field.name === hasMany.criteriaChainAttribute
      );

      if (!actualField) return {};
    }

    currentField.extra = {
      getAsyncOptions: async () => {
        try {
          const res = await defaultFetch(`/Field/${field.id}/FindDomains`);

          const options = res.map((option) => ({
            label: getResourceTo(option.description),
            value: option.value
          }));

          return options;
        } catch (error) {
          console.error('Failed to fetch field domains:', error);
          return [];
        }
      }
    };
  }

  if (field.name === 'route.status') {
    currentField.extra = {
      options: getRouteStatusOptions()
    };
  }

  if (fieldDates.includes(currentField.type)) {
    currentField.extra = {
      locale: userPreferences.locale,
      dateFormat: userPreferences.dateFormat
    };
  }

  if (fieldForDateTimeOrTime.includes(currentField.type)) {
    currentField.extra = {
      locale: userPreferences.locale,
      timeFormat: userPreferences.timeFormat
    };
  }

  return currentField;
};

const getDefaultFilterByColumn = (column) => {
  if (column === 'SpeedAssignmentRoute') {
    return {
      defaultSelected: ['route.organization', 'route.status'],
      defaultValues: undefined
    };
  }
  return {
    defaultSelected: ['organization', 'enabled'],
    defaultValues: { enabled: true }
  };
};

export const getDefaultFilters = (column) => {
  const storageFilters = Monet.Session.sessionStorage.filters;
  const sessionUserId = Monet.Session.User.id;
  const filtersParsed = JSON.parse(storageFilters ?? '{}');
  const filterColumn = filtersParsed[sessionUserId]?.[column];

  if (!filterColumn || Object.keys(filterColumn).length === 0) {
    return getDefaultFilterByColumn(column);
  }

  const parseDateOrTime = (val) => {
    if (isDate(val)) {
      const date = moment(val).toDate();
      return date;
    }
    if (isTime(val)) return convertHourToSeconds(val);
    return val;
  };

  const parseValue = (value) => {
    if (!value) return undefined;

    if (typeof value === 'object' && (value?.start || value?.end)) {
      return {
        start: parseDateOrTime(value.start),
        end: parseDateOrTime(value.end)
      };
    }

    if (typeof value === 'string') {
      return parseDateOrTime(value);
    }

    if (
      typeof value === 'object' &&
      Object.keys(value).length > 0 &&
      Object.keys(value)[0] === 'value'
    ) {
      return parseDateOrTime(value.value) || undefined;
    }

    return value;
  };

  const defaultValues = Object.entries(filterColumn).reduce(
    (acc, [key, value]) => {
      const newValue = parseValue(value);
      if (newValue) {
        acc[key] = newValue;
      }
      return acc;
    },
    {}
  );

  const defaultSelected = Object.keys(filterColumn);

  return {
    defaultValues,
    defaultSelected
  };
};

export const saveFiltersByColumn = (column, values) => {
  const sessionUserId = Monet.Session.User.id;
  const storageFilters = Monet.Session.sessionStorage.filters || '{}';
  const filters = JSON.parse(storageFilters);

  if (!filters[sessionUserId]) {
    filters[sessionUserId] = {};
  }

  if (!filters[sessionUserId][column]) {
    filters[sessionUserId][column] = {};
  }

  filters[sessionUserId][column] = values;

  Monet.Session.sessionStorage.filters = JSON.stringify(filters);

  const userPreferences = new Greenmile.UserPreferences.Actions();
  userPreferences.saveOrUpdateSingleUserPreference('filters');
};
